'use client';

import React, { useState, useCallback, useMemo } from 'react';
import BigNumber from 'bignumber.js';
import { MdSwapHoriz } from 'react-icons/md';

import useQueryWithPages from 'src/hooks/useQueryWithPages';

import { formatTimestamp } from '../../utils/formatTimestamp';
import styles from './TokenTransfersPage.module.css';
import FilterDropdown from 'src/app/components/FilterDropdown';
import type { FilterSection } from 'src/app/components/FilterDropdown';

const TOKEN_TYPE_OPTIONS: FilterSection = {
  title: 'Token Type',
  type: 'checkbox',
  key: 'type',
  options: [
    { value: 'ORC-20', label: 'ORC-20' },
    { value: 'ORC-721', label: 'ORC-721' },
    { value: 'ORC-1155', label: 'ORC-1155' },
    { value: 'ORC-404', label: 'ORC-404' },
  ],
};

const TokenTransfersPage: React.FC = () => {
  const [filterValues, setFilterValues] = useState<Record<string, Array<string>>>({ type: [] });

  const filters = useMemo(() => {
    const f: Record<string, any> = {};
    if (filterValues.type?.length) f.type = filterValues.type;
    return f;
  }, [filterValues]);

  const handleFilterApply = useCallback((values: Record<string, Array<string>>) => {
    setFilterValues(values);
  }, []);

  const handleFilterReset = useCallback(() => {
    setFilterValues({ type: [] });
  }, []);

  const transfersQuery = useQueryWithPages({
    resourceName: 'token_transfers_all',
    filters,
  });

  const transfers = React.useMemo(() => {
    return transfersQuery.data?.items || [];
  }, [transfersQuery.data?.items]);

  const formatAmount = (total: any, token: any) => {
    if (!total?.value) return '0';
    const decimals = token?.decimals ? parseInt(token.decimals, 10) : 18;
    const bn = BigNumber(total.value);
    const formatted = bn.dividedBy(BigNumber(10).pow(decimals));
    if (formatted.isGreaterThanOrEqualTo(1000000)) {
      return `${formatted.dividedBy(1000000).toFixed(2)}M`;
    }
    if (formatted.isGreaterThanOrEqualTo(1000)) {
      return `${formatted.dividedBy(1000).toFixed(2)}K`;
    }
    if (formatted.isLessThan(0.000001) && !formatted.isZero()) {
      return formatted.toExponential(2);
    }
    return formatted.toFixed(4);
  };

  return (
    <div className={ styles.container }>
      <div className={ styles.header }>
        <h1 className={ styles.title }>Token Transfers</h1>
        <FilterDropdown
          label="Advanced filter"
          sections={[TOKEN_TYPE_OPTIONS]}
          values={filterValues}
          onApply={handleFilterApply}
          onReset={handleFilterReset}
        />
      </div>

      <div className={ styles.tableContainer }>
        <table className={ styles.table }>
          <thead>
            <tr>
              <th>Txn hash</th>
              <th>Method</th>
              <th>Block</th>
              <th>From</th>
              <th>To</th>
              <th>Token</th>
              <th>Amount</th>
            </tr>
          </thead>
          <tbody>
            { transfersQuery.isLoading && transfers.length === 0 && (
              Array.from({ length: 10 }).map((_, i) => (
                <tr key={ `skeleton-${ i }` }>
                  { Array.from({ length: 7 }).map((__, j) => (
                    <td key={ j }>
                      <div className={ styles.skeleton } style={{ width: `${ 50 + (j * 7) % 40 }%`, height: '14px' }}/>
                      { j === 0 && <div className={ styles.skeleton } style={{ width: '40%', height: '10px', marginTop: '6px' }}/> }
                    </td>
                  )) }
                </tr>
              ))
            ) }
            { !transfersQuery.isLoading && transfers.length === 0 && (
              <tr>
                <td colSpan={ 7 }>
                  <div className={ styles.emptyState }>
                    <MdSwapHoriz size={ 48 }/>
                    <p>No token transfers found.</p>
                  </div>
                </td>
              </tr>
            ) }
            { transfers.map((transfer: any, index: number) => (
              <tr key={ transfer.transaction_hash || index }>
                <td>
                  <a href={ `/tx/${ transfer.transaction_hash }` } className={ styles.link }>
                    { transfer.transaction_hash?.slice(0, 10) }...{ transfer.transaction_hash?.slice(-4) }
                  </a>
                  <div className={ styles.age }>{ formatTimestamp(transfer.timestamp) }</div>
                </td>
                <td>
                  <span className={ styles.method }>{ transfer.method || 'Transfer' }</span>
                </td>
                <td>
                  <a href={ `/block/${ transfer.block_number }` } className={ styles.link }>
                    { transfer.block_number }
                  </a>
                </td>
                <td>
                  <a href={ `/address/${ transfer.from?.hash }` } className={ styles.address }>
                    { transfer.from?.hash?.slice(0, 8) }...{ transfer.from?.hash?.slice(-6) }
                  </a>
                </td>
                <td>
                  <a href={ `/address/${ transfer.to?.hash }` } className={ styles.address }>
                    { transfer.to?.hash?.slice(0, 8) }...{ transfer.to?.hash?.slice(-6) }
                  </a>
                </td>
                <td>
                  <a href={ `/token/${ transfer.token?.address }` } className={ styles.tokenLink }>
                    { transfer.token?.name || transfer.token?.symbol || 'Unknown' }
                  </a>
                </td>
                <td className={ styles.amount }>
                  { formatAmount(transfer.total, transfer.token) } { transfer.token?.symbol || '' }
                </td>
              </tr>
            )) }
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      { transfers.length > 0 && (
        <div className={ styles.pagination }>
          <button
            className={ styles.pageButton }
            disabled={ !transfersQuery.pagination.canGoBackwards }
            onClick={ transfersQuery.pagination.onPrevPageClick }
          >
            First
          </button>
          <button
            className={ styles.pageButton }
            disabled={ !transfersQuery.pagination.canGoBackwards }
            onClick={ transfersQuery.pagination.onPrevPageClick }
          >
            &lt;
          </button>
          <button className={ `${ styles.pageButton } ${ styles.pageButtonActive }` }>
            { transfersQuery.pagination.page }
          </button>
          <button
            className={ styles.pageButton }
            disabled={ !transfersQuery.pagination.hasNextPage }
            onClick={ transfersQuery.pagination.onNextPageClick }
          >
            &gt;
          </button>
        </div>
      ) }
    </div>
  );
};

export default TokenTransfersPage;
